import { DateFilterTypesEnum } from '@propertelligent/client-api';
import getUserLocale from 'get-user-locale';

class DateHelper {
  userLocale = getUserLocale();
  defaultFormat: string;
  constructor() {
    this.defaultFormat = 'yyyy/MM/dd HH:mm a';
  }

  formatDOBString = (dateTime: string) => {
    const dateValue = dateTime.split('-');

    return `${dateValue[1]}/${dateValue[2]}/${dateValue[0]}`;
  };//End of method

  /**
   * Input  : yyyy-mm-ddThh:mm:ss. SSSZ  
   * Output : en-GB --> dd/mm/yyyy, hh:mm:ss a ||
   * Output : en-US --> mm/dd/yyyy, hh:mm:ss a
  */
  convertUTCtoDateTime = (utcDateTime: string, showTime: boolean = false): string | null => {
    if (utcDateTime) {
      const utcDate = new Date(utcDateTime);

      // Format the date to local datetime string
      const localDateTimeString = showTime ? new Intl.DateTimeFormat(this.userLocale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true,
        // timeZone: 'America/New_York', // Change this to your desired time zone
        // timeZoneName: 'short'
      }).format(utcDate) : new Intl.DateTimeFormat(this.userLocale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour12: true,
        // timeZone: 'America/New_York', // Change this to your desired time zone
        // timeZoneName: 'short'
      }).format(utcDate);
      return localDateTimeString;
    }
    else return '--';
  }; //End of method

  /**
   * Input  : yyyy-mm-ddThh:mm:ss. SSSZ  
   * Output : en-GB --> 2 Apr 2024, 8:17 pm ||
   * Output : en-US --> Apr 2, 2024, 8:17 PM
  */
  convertUTCtoDateTimeSimple = (utcDateTime: string, showTime: boolean = false,): string | null => {
    if (utcDateTime) {
      const dateObject = new Date(utcDateTime);

      const formattedDate = showTime ? dateObject.toLocaleString(this.userLocale, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }) : dateObject.toLocaleString(this.userLocale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return formattedDate;
    }
    else return '--';
  }; //End of method


  convertUTCtoDateTime2 = (utcDateTime: string, showTime: boolean = false): string | null => {
    if (utcDateTime) {
      const utcDate = new Date(utcDateTime);

      // Get the day, month, and year
      const day = utcDate.getDate().toString().padStart(2, '0'); // Ensure 2 digits
      const month = (utcDate.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
      const year = utcDate.getFullYear();

      // Format the date as dd-mm-yyyy
      const formattedDate = `${year}-${month}-${day}`;

      if (showTime) {
        // If time is needed, format the time as well
        const hours = utcDate.getHours().toString().padStart(2, '0');
        const minutes = utcDate.getMinutes().toString().padStart(2, '0');
        const seconds = utcDate.getSeconds().toString().padStart(2, '0');
        const time = `${hours}:${minutes}:${seconds}`;

        return `${formattedDate} ${time}`;
      } else {
        return formattedDate;
      }
    } else {
      return '--';
    }
  };

  formatDateonly = (dateString: any) => {

    if (!dateString) {
      return ''; // Handle undefined or empty date string
    }

    let date: Date;

    try {
      // Check if the date string is in ISO format
      if (dateString.includes("T")) {
        date = new Date(dateString);
      } else {
        // Otherwise, manually parse the date string
        const parts = dateString.split(" ");
        const dateParts = parts[0].split("/");
        const timeParts = parts[1]?.split(":") || ["00", "00"]; // Default to "00:00" if time is missing

        if (dateParts.length !== 3 || timeParts.length !== 2) {
          throw new Error('Invalid date format');
        }

        const month = parseInt(dateParts[0], 10);
        const day = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);

        date = new Date(year, month - 1, day, hours, minutes);
      }

      if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
      }

      // Formatting the date
      const monthName = date.toLocaleString("default", { month: "short" });
      const day = date.getDate();
      const year = date.getFullYear();

      const formattedDate = `${monthName} ${day}, ${year}`;

      // Formatting the time
      const formattedTime = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      return `${formattedDate}`;
    } catch (error) {
      console.error('Error parsing date:', error);
      return ''; // Handle error case as needed
    }
  };

  getClientDate = () => {
    return (new Date()).toISOString()
  }
  formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return ""; // Handle invalid dates
    return date.toISOString().split("T")[0];
  };
  getDateFilterType = (predefinedFilter: string) => {
    switch (predefinedFilter) {
      case "Last 7 Days":
        return DateFilterTypesEnum.Last7Days;

      case "Last 14 Days":
        return DateFilterTypesEnum.Last14Days;

      case "Last Month":
        return DateFilterTypesEnum.LastMonth;

      case "Last 3 Months":
        return DateFilterTypesEnum.Last3Months;

      case "Last 6 Months":
        return DateFilterTypesEnum.Last6Months;

      case "Last Year":
        return DateFilterTypesEnum.LastYear;

      default:
        return DateFilterTypesEnum.Custom
    }
  }

  /**
   * Mirrors the backend's DateFilterBM.GetFilterDateRange so a relative report duration
   * ("Last Month", "Last 7 Days", ...) can be shown as the exact From/To dates it covers.
   */
  getDateFilterRange = (
    dateFilter: { dateFilterTypeId?: number; startDate?: string | Date | null; endDate?: string | Date | null } | null | undefined,
    clientDate: Date = new Date(),
  ): { startDate: Date | null; endDate: Date | null } => {
    if (!dateFilter) return { startDate: null, endDate: null };
    const toDate = (value) => (value ? new Date(value) : null);
    const y = clientDate.getUTCFullYear();
    const m = clientDate.getUTCMonth();
    const d = clientDate.getUTCDate();
    const utc = (year: number, month: number, day: number) => new Date(Date.UTC(year, month, day, 12));
    // Like .NET AddMonths: clamp the day to the target month's length instead of overflowing.
    const addMonths = (months: number) => {
      const daysInTarget = new Date(Date.UTC(y, m + months + 1, 0)).getUTCDate();
      return utc(y, m + months, Math.min(d, daysInTarget));
    };
    const today = utc(y, m, d);
    switch (Number(dateFilter.dateFilterTypeId)) {
      case DateFilterTypesEnum.Last7Days:
        return { startDate: utc(y, m, d - 7), endDate: today };
      case DateFilterTypesEnum.Last14Days:
        return { startDate: utc(y, m, d - 14), endDate: today };
      case DateFilterTypesEnum.MonthToDate:
        return { startDate: utc(y, m, 1), endDate: today };
      case DateFilterTypesEnum.LastMonth:
        return { startDate: utc(y, m - 1, 1), endDate: utc(y, m, 0) };
      case DateFilterTypesEnum.Last3Months:
        return { startDate: addMonths(-3), endDate: today };
      case DateFilterTypesEnum.Last6Months:
        return { startDate: addMonths(-6), endDate: today };
      case DateFilterTypesEnum.YearToDate:
        return { startDate: utc(y, 0, 1), endDate: today };
      case DateFilterTypesEnum.LastYear:
        return { startDate: utc(y - 1, 0, 1), endDate: utc(y - 1, 11, 31) };
      case DateFilterTypesEnum.Next30Days:
        return { startDate: today, endDate: utc(y, m, d + 30) };
      case DateFilterTypesEnum.Next3Months:
        return { startDate: today, endDate: addMonths(3) };
      case DateFilterTypesEnum.Next6Months:
        return { startDate: today, endDate: addMonths(6) };
      case DateFilterTypesEnum.Last12Months:
        return { startDate: addMonths(-12), endDate: today };
      default:
        return { startDate: toDate(dateFilter.startDate), endDate: toDate(dateFilter.endDate) };
    }
  };

  formatISOToMMDDYYYY = (isoDateString: string): string => {
  if (!isoDateString) return '';

  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return ''; // Invalid date check

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};
formatISOToMMDDYYYYTime = (isoDateString: string): string => {
  if (!isoDateString) return "";

  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return "";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12;

  return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
};

 formatToCustomDateTime = (utcDateTime: string): string => {
  if (!utcDateTime) return '--';

  const date = new Date(utcDateTime);

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  // This will give format like: "Jan 24, 2025, 11:32 AM"
  let formatted = date.toLocaleString("en-US", options);

  // Remove the comma between date and time, and make AM/PM lowercase
  formatted = formatted.replace(',', '').replace('AM', 'am').replace('PM', 'pm');

  return formatted;
};
 formatToCustomDate = (utcDateTime: string): string => {
  if (!utcDateTime) return '--';

  const date = new Date(utcDateTime);

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };

  // This will give format like: "Jan 24, 2025"
  let formatted = date.toLocaleString("en-US", options);

  
  formatted = formatted.replace(',', '');

  return formatted;
};


  getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}-${hours}${minutes}${seconds}`;
  };

  getLocalISOString = (date) => {
    const tzOffset = date.getTimezoneOffset() * 60000; // in ms
    const localDate = new Date(date.getTime() - tzOffset);
    return localDate.toISOString(); // returns full ISO string WITHOUT shifting time
  };

  // Get previous day in format mm/dd/yyyy
  getPreviousDay = (dateString: string) => {
    if (!dateString) return new Date().toLocaleDateString("en-US");
    if (dateString.includes('T')) {
        dateString = dateString.split('T')[0];
    }
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() - 1);
    return date.toLocaleDateString("en-US");
  };

  // Today's date as YYYY-MM-DD in the user's *local* timezone, for <input type="date"> defaults.
  // toISOString() would give the UTC date, which lags/leads the local date for part of every day.
  getTodayLocalDateString = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  };

  getTomorrowDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
}

export const convertUTCToDatetimeLocalInputValue = (
  utcIso?: string | null,
): string => {
  if (!utcIso) {
    return "";
  }

  const date = new Date(utcIso);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const convertDatetimeLocalInputValueToUTC = (
  localValue?: string | null,
): string | null => {
  if (!localValue) {
    return null;
  }

  const date = new Date(localValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

export function formatChatDate(inputDate) {
  const d = (inputDate instanceof Date) ? inputDate : new Date(inputDate);
  const now = new Date();
  const isSameDay = d.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYest = d.toDateString() === yesterday.toDateString();

  if (isSameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  }
  if (isYest) return 'Yesterday';

  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}


// export const formatToCustomDateTime = (utcDateTime: string): string => {
//   if (!utcDateTime) return '--';

//   const date = new Date(utcDateTime);

//   const options: Intl.DateTimeFormatOptions = {
//     month: "short",
//     day: "numeric",
//     year: "numeric",
//     hour: "numeric",
//     minute: "2-digit",
//     hour12: true,
//   };

//   // This will give format like: "Jan 24, 2025, 11:32 AM"
//   let formatted = date.toLocaleString("en-US", options);

//   // Remove the comma between date and time, and make AM/PM lowercase
//   formatted = formatted.replace(',', '').replace('AM', 'am').replace('PM', 'pm');

//   return formatted;
// };


export default new DateHelper();
